import { Shield, Zap, Globe, Lock, Cpu, FileCheck, Map, Layers } from "lucide-react";
import { t, type TranslationKey } from "@/lib/i18n";
import { useLocale } from "@/hooks/use-locale";

const FEATURES = [
  { icon: Zap,       titleKey: "ft.fast",    descKey: "ft.fastD" },
  { icon: Lock,      titleKey: "ft.private", descKey: "ft.privateD" },
  { icon: Globe,     titleKey: "ft.offline", descKey: "ft.offlineD" },
  { icon: Shield,    titleKey: "ft.free",    descKey: "ft.freeD" },
  { icon: Cpu,       titleKey: "ft.proj",    descKey: "ft.projD" },
  { icon: FileCheck, titleKey: "ft.format",  descKey: "ft.formatD" },
  { icon: Map,       titleKey: "ft.preview", descKey: "ft.previewD" },
  { icon: Layers,    titleKey: "ft.tools",   descKey: "ft.toolsD" },
] as const;

const Features = () => {
  useLocale(); // re-render on language change
  const stack: Array<[TranslationKey, string]> = [
    ["ft.runtime",    t("ft.runtimeV")],
    ["ft.geospatial", "shpjs, @mapbox/shp-write, Turf.js v7, proj4js"],
    ["ft.mapPreview", t("ft.mapPreviewV")],
    ["ft.bundler",    t("ft.bundlerV")],
    ["ft.styling",    t("ft.stylingV")],
  ];
  return (
  <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

    {/* Feature grid */}
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
      gap: 12,
    }}>
      {FEATURES.map(({ icon: Icon, titleKey, descKey }) => (
        <div
          key={titleKey}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "14px 16px",
            borderRadius: 12,
            border: "1px solid hsl(var(--border))",
            background: "hsl(var(--card))",
          }}
        >
          {/* Icon chip */}
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: "hsl(var(--brand) / 0.10)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
            <Icon style={{ width: 16, height: 16, color: "hsl(var(--brand-strong))" }} />
          </div>

          {/* Text */}
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 4, lineHeight: 1.3 }}>
              {t(titleKey)}
            </p>
            <p style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", lineHeight: 1.55 }}>
              {t(descKey)}
            </p>
          </div>
        </div>
      ))}
    </div>

    {/* Divider */}
    <div style={{ borderTop: "1px solid hsl(var(--border))", margin: "24px 0 16px" }} />

    {/* Stack info */}
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "hsl(var(--brand-strong))" }}>
        {t("ft.techStack")}
      </p>
      {stack.map(([labelKey, value]) => (
        <div key={labelKey} style={{ display: "flex", gap: 8, fontSize: 11 }}>
          <span style={{ fontWeight: 700, color: "hsl(var(--foreground))", minWidth: 88, flexShrink: 0 }}>{t(labelKey)}</span>
          <span style={{ color: "hsl(var(--muted-foreground))" }}>{value}</span>
        </div>
      ))}
    </div>

    {/* Divider */}
    <div style={{ borderTop: "1px solid hsl(var(--border))", margin: "20px 0 16px" }} />

    {/* Developer credit */}
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 8,
    }}>
      <div>
        <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "hsl(var(--muted-foreground))", marginBottom: 3 }}>
          {t("ft.devBy")}
        </p>
        <p style={{ fontSize: 15, fontWeight: 800, color: "hsl(var(--foreground))", letterSpacing: "-0.01em" }}>
          Jokopal
        </p>
      </div>
      <div style={{ textAlign: "right" }}>
        <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "hsl(var(--muted-foreground))", marginBottom: 3 }}>
          {t("ft.license")}
        </p>
        <p style={{ fontSize: 11, color: "hsl(var(--foreground))", fontWeight: 600 }}>{t("ft.licenseVal")}</p>
      </div>
    </div>

    {/* Trademark-style footer line */}
    <p style={{ fontSize: 9, color: "hsl(var(--muted-foreground) / 0.55)", marginTop: 12, letterSpacing: "0.06em" }}>
      {t("ft.footer")}
    </p>
  </div>
  );
};

export default Features;
